import { ActionTree, Store, ActionObject, Action, ActionContext, Module, MutationTree, GetterTree } from 'vuex';
import { RootState } from '@/store';

type MutationDef<ModuleState, T> = (state: ModuleState, payload: T) => void;
type MappedMutationDef<T> = (payload: T) => void;

type ActionDef<Root, ModuleState, T, R> = (
    this: Store<Root>,
    context: ActionContext<ModuleState, Root>,
    payload: T
) => Promise<R>;
type MappedActionDef<T, R> = (payload: T) => Promise<R>;

type ParamlessActionDef<Root, ModuleState, R> = (
    this: Store<Root>,
    context: ActionContext<ModuleState, Root>
) => Promise<R>;
type ParamlessMappedActionDef<R> = () => Promise<R>;

type GetterDef<Root, ModuleState, OtherGetters, RootGetters, ReturnType> = (
    this: Store<Root>,
    state: ModuleState,
    getters: OtherGetters,
    RootState: Root,
    RootGetters: RootGetters
) => ReturnType;
type MappedGetterDef<ReturnType> = ReturnType;

type ParameterizedGetterDef<Root, ModuleState, ParamType, ReturnType> = (
    this: Store<Root>,
    state: ModuleState,
    param: ParamType
) => ReturnType;
type ParameterizedMappedGetterDef<ParamType, ReturnType> = (param?: ParamType) => ReturnType;

export interface RawModuleDefinition<Getters, Actions, Mutations, Modules, ModuleState> {
    state: ModuleState;
    getters: {
        [P in keyof Getters]:
        Getters[P] extends GetterDef<RootState, ModuleState, infer OtherGetters, infer RootGetters, infer ReturnType> ?
        GetterDef<RootState, ModuleState, OtherGetters, RootGetters, ReturnType> :
        Getters[P] extends ParameterizedGetterDef<
            RootState,
            ModuleState,
            infer ParameterizedParamType,
            infer ParameterizedReturnType
        > ?
        ParameterizedGetterDef<RootState, ModuleState, ParameterizedParamType, ParameterizedReturnType> :
        Getters[P]
    };
    actions: {
        [P in keyof Actions]:
        Actions[P] extends ParamlessActionDef<RootState, ModuleState, infer ParamlessReturnType> ?
        ParamlessActionDef<RootState, ModuleState, ParamlessReturnType> :
        Actions[P] extends ActionDef<RootState, ModuleState, infer PayloadType, infer ReturnType> ?
        ActionDef<RootState, ModuleState, PayloadType, ReturnType> :
        Actions[P]
    };
    mutations: Mutations;
    modules: {
        [P in keyof Modules]:
        Modules[P] extends RawModuleDefinition<infer G, infer A, infer M, infer Mod, infer S> ?
        RawModuleDefinition<G, A, M, Mod, S> :
        Modules[P];
    };
}

export type TypedModule<Getters, Actions, Mutations, Modules, ModuleState, Root>
    = RawModuleDefinition<Getters, Actions, Mutations, Modules, ModuleState>
    & Module<ModuleState, Root>;

export interface MappedTypedModule<Getters, Actions, Mutations, Modules, Root, ModuleState> {
    actions: {
        [P in keyof Actions]:
        Actions[P] extends ParamlessActionDef<RootState, ModuleState, infer ParamlessReturnType> ?
        ParamlessMappedActionDef<ParamlessReturnType> :
        Actions[P] extends ActionDef<Root, ModuleState, infer Payload, infer ReturnType> ?
        MappedActionDef<Payload, ReturnType> :
        Actions[P]
    };
    mutations: {
        [P in keyof Mutations]:
        Mutations[P] extends MutationDef<ModuleState, infer T> ?
        MappedMutationDef<T> :
        Mutations[P]
    };
    getters: {
        [P in keyof Getters]:
        Getters[P] extends GetterDef<RootState, ModuleState, infer OtherGetters, infer RootGetters, infer ReturnType> ?
        MappedGetterDef<ReturnType> :
        Getters[P] extends ActionDef<Root, ModuleState, infer ParamType, infer ParameterizedReturnType> ?
        ParameterizedMappedGetterDef<ParamType, ParameterizedReturnType> :
        Getters[P]
    };
    modules: {
        [P in keyof Modules]:
        Modules[P] extends RawModuleDefinition<infer G, infer A, infer M, infer Mod, infer S> ?
        MappedTypedModule<G, A, M, Mod, Root, S> :
        Modules[P]
    };
    state: ModuleState;
}

interface ModuleConfig<Root> {
    name: string;
    namespace: string;
    namespaced: boolean;
    store: Store<Root>;
}

function MapAction<ModuleState, Root, T, R>(config: ModuleConfig<Root>, func: ActionDef<Root, ModuleState, T, R>)
    : MappedActionDef<T, R> {
    let name = config.name;
    if (config.namespaced) {
        name = `${config.namespace}${config.name}`;
    }
    return (payload: T) => config.store.dispatch(name, payload) as Promise<R>;
}

function MapMutation<ModuleState, Root, T>(config: ModuleConfig<Root>, func: MutationDef<ModuleState, T>)
    : MappedMutationDef<T> {
    let name = config.name;
    if (config.namespaced) {
        name = `${config.namespace}${config.name}`;
    }
    return (payload: T) => config.store.commit(name, payload);
}

function MapGetter<ModuleState,
    Root,
    OtherGetters,
    RootGetters,
    T,
    R>(config: ModuleConfig<Root>, func: GetterDef<
        Root,
        ModuleState,
        OtherGetters,
        RootGetters,
        R>)
    : ParameterizedMappedGetterDef<T, R> {
    let name = config.name;
    if (config.namespaced) {
        name = `${config.namespace}${config.name}`;
    }
    const property = Object.getOwnPropertyDescriptor(config.store.getters, name);
    if (property && property.get) {
        return (param?: T) => {
            return config.store.getters[name];
        };
    }
    return (param?: T) => {
        return config.store.getters[name](param);
    };
}

function isActionObject<S, R>(action: Action<S, R>): action is ActionObject<S, R> {
    // Ignore 'always defined' error.  It's only 'always defined' because of the cast,
    // which may not be valid.  The cast exists to allow the property access,
    // which is to determine if the action is actually that type
    // @ts-ignore
    if ((action as ActionObject<S, R>).handler) {
        return true;
    }
    return false;
}

export function markModule<Getters, Actions, Mutations, Modules, ModuleState, Root>(
    obj: TypedModule<Getters, Actions, Mutations, Modules, ModuleState, Root>,
    rootState: Root):
    TypedModule<Getters, Actions, Mutations, Modules, ModuleState, Root> {
    return obj;
}

export function markRoot<Getters, Actions, Mutations, Modules, Root>(
    obj: TypedModule<Getters, Actions, Mutations, Modules, Root, Root>):
    TypedModule<Getters, Actions, Mutations, Modules, Root, Root> {
    return obj;
}

export function createModule<Getters, Actions, Mutations, Modules, ModuleState, Root>(
    obj: TypedModule<Getters, Actions, Mutations, Modules, ModuleState, Root>,
    namespace: string | null,
    store: Store<Root>):
    MappedTypedModule<Getters, Actions, Mutations, Modules, Root, ModuleState> {
    const actions = obj.actions as (Actions & ActionTree<ModuleState, Root>);
    const newActions = {} as any;
    for (const key in actions) {
        if (actions[key]) {
            let action = actions[key];
            if (isActionObject(action)) {
                action = action.handler;
            }
            const config = {
                name: key,
                namespaced: obj.namespaced || false,
                namespace: namespace || '',
                store
            };
            newActions[key] = MapAction(config, action);
        }
    }
    const mutations = obj.mutations as (Mutations & MutationTree<ModuleState>);
    const newMutations = {} as any;
    for (const key in mutations) {
        if (mutations[key]) {
            const config = {
                name: key,
                namespaced: obj.namespaced || false,
                namespace: namespace || '',
                store
            };
            const mutation = mutations[key];
            newMutations[key] = MapMutation(config, mutation);
        }
    }

    const getters = obj.getters as (Getters & GetterTree<ModuleState, Root>);
    const newGetters = {} as any;
    for (const key in getters) {
        if (getters[key]) {
            const config = {
                name: key,
                namespaced: obj.namespaced || false,
                namespace: namespace || '',
                store
            };
            const getter = getters[key];
            const mapped = MapGetter(config, getter);
            Object.defineProperty(newGetters, key, { get: mapped });
        }
    }

    const newModules = {} as any;
    const modules = obj.modules;
    for (const key in modules) {
        if (modules[key]) {
            const moduleNamespace = `${namespace}${key}/`;
            const module = modules[key];

            newModules[key] = createModule(module as any, moduleNamespace, store);
        }
    }

    return {
        actions: newActions,
        mutations: newMutations,
        getters: newGetters,
        modules: { ...newModules },
        state: obj.state
    };
}

export function createStore<Getters, Actions, Mutations, Modules, StoreRootState>(
    obj: TypedModule<Getters, Actions, Mutations, Modules, StoreRootState, StoreRootState>,
    store: Store<StoreRootState>
)
    : MappedTypedModule<Getters, Actions, Mutations, Modules, StoreRootState, StoreRootState> {
    return createModule(obj, '', store);
}
